/**
 * Discovery Queue Schema
 * 
 * Defines the complete structure for discovery queue items including
 * UX configuration, AI messaging, and template specifications.
 */

/**
 * Discovery UX Template Types
 */
export const DISCOVERY_TEMPLATE_TYPES = {
  SINGLE_ROW_SELECTOR: 'SingleRowSelectorTemplate',
  MULTI_ROW_SELECTOR: 'MultiRowSelectorTemplate', 
  DISTRIBUTION_BAR_CHART: 'DistributionBarChartTemplate',
  INLINE_SLIDER: 'InlineSliderTemplate'
};

/**
 * Discovery Status Types
 */
export const DISCOVERY_STATUS = {
  PENDING: 'discovery_pending',
  IN_PROGRESS: 'discovery_in_progress',
  DONE: 'discovery_done',
  SKIPPED: 'discovery_skipped'
};

/**
 * Complete Discovery Queue Item Schema
 * 
 * @typedef {Object} DiscoveryQueueItem
 * @property {string} topic - Topic identifier (skills, location, experience, etc.)
 * @property {Object} icp_requirement - ICP requirement object
 * @property {string} icp_requirement.section_id - Unique section identifier
 * @property {string} icp_requirement.section_name - Display name for section
 * @property {string} icp_requirement.bool_str - Boolean search string
 * @property {string} icp_requirement.section_type - "Must Have" or "Nice to Have"
 * @property {string} status - Current discovery status
 * @property {Object} discovery_config - Complete UX and messaging configuration
 * @property {string} discovery_config.template_type - Which template to use
 * @property {string} discovery_config.ai_intro_message - AI message before showing component
 * @property {string} discovery_config.ai_confirmation_message - AI message after confirmation
 * @property {Object} discovery_config.ux_config - Template-specific configuration
 * @property {string} discovery_config.ux_config.title - Component title text
 * @property {string} discovery_config.ux_config.subtitle - Component subtitle (optional)
 * @property {string} discovery_config.ux_config.chart_title - Chart/card title (for chart templates)
 * @property {string} discovery_config.ux_config.chart_subtitle - Chart/card subtitle (for chart templates)
 * @property {string} discovery_config.ux_config.confirm_button_text - Confirmation button text
 * @property {Object} discovery_config.template_props - Template-specific props
 * @property {Object} discovery_result - Stored result after confirmation (optional)
 * @property {number} created_at - Timestamp when item was created
 * @property {number} updated_at - Timestamp when item was last updated
 */

/**
 * Example Discovery Queue Items for each template type
 */
export const EXAMPLE_QUEUE_ITEMS = {
  
  // Skills Discovery - Single Row Selector
  skills: {
    topic: "skills",
    icp_requirement: {
      section_id: "skills_must_have",
      section_name: "Must Have Skills",
      bool_str: "(skills.name:(React OR JavaScript OR Node.js OR Python))",
      section_type: "Must Have"
    },
    status: DISCOVERY_STATUS.PENDING,
    discovery_config: {
      template_type: DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR,
      ai_intro_message: "Based on your role description, I've identified some key technical skills. Let me confirm these with you.",
      ai_confirmation_message: "Perfect! I've noted your required skills. This will help us find candidates with the right technical background.",
      ux_config: {
        title: "Got it! Just to confirm — you're looking for candidates with the following skills, right? 👇",
        confirm_button_text: "Confirm Skills"
      },
      template_props: {
        items: ["React", "JavaScript", "Node.js", "Python", "TypeScript", "AWS"],
        selectedItems: ["React", "JavaScript", "Node.js", "Python"]
      }
    },
    discovery_result: null,
    created_at: Date.now(),
    updated_at: Date.now()
  },

  // Location Discovery - Multi Row Selector  
  location: {
    topic: "location",
    icp_requirement: {
      section_id: "location_preferences",
      section_name: "Location Preferences", 
      bool_str: "(location.city:(San Francisco OR Vancouver) OR remote:true)",
      section_type: "Must Have"
    },
    status: DISCOVERY_STATUS.PENDING,
    discovery_config: {
      template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
      ai_intro_message: "Now let's talk about location preferences. I've analyzed where similar candidates are typically found.",
      ai_confirmation_message: "Great choice! I've updated your location preferences. This will help us target the right talent pools.",
      ux_config: {
        title: "Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍\nPick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨",
        confirm_button_text: "Confirm Location"
      },
      template_props: {
        sections: [
          {
            title: "Common Locations",
            items: [
              { label: "San Francisco, CA, USA (50%)", value: "sf" },
              { label: "Vancouver, BC, Canada (20%)", value: "vancouver" },
              { label: "Bengaluru, Karnataka, India (10%)", value: "bengaluru" },
              { label: "Johannesburg, GT, South Africa (5%)", value: "johannesburg" }
            ]
          },
          {
            title: "or select a remote option",
            items: [
              { label: "Remote (Global)", value: "remote_global" },
              { label: "Remote (US only)", value: "remote_us" }
            ]
          }
        ],
        selectedItems: ["sf", "vancouver", "remote_global"] // Pre-select all items by default
      }
    },
    discovery_result: null,
    created_at: Date.now(),
    updated_at: Date.now()
  },

  // Company Stage Discovery - Distribution Bar Chart
  company_stage: {
    topic: "company_stage", 
    icp_requirement: {
      section_id: "company_stage_preferences",
      section_name: "Company Stage Experience",
      bool_str: "(company.stage:(Late Stage OR Early Stage))",
      section_type: "Nice to Have"
    },
    status: DISCOVERY_STATUS.PENDING,
    discovery_config: {
      template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
      ai_intro_message: "Let me show you the company stage distribution of your current workforce to help guide your preferences.",
      ai_confirmation_message: "Excellent! I've noted your company stage preferences. This will help us find candidates with relevant experience.",
      ux_config: {
        title: "I reviewed your existing workforce, and this is the distribution across different company stages. Which company stage(s) would you like to target?",
        chart_title: "Recent Company Stages",
        chart_subtitle: "Employees who recently worked at these company stages",
        confirm_button_text: "Select at Least One Option"
      },
      template_props: {
        data: [
          { name: "Late Stage", percentage: 60 },
          { name: "Early Stage", percentage: 20 },
          { name: "Mid Stage", percentage: 15 },
          { name: "Public", percentage: 5 }
        ],
        selectedItems: []
      }
    },
    discovery_result: null,
    created_at: Date.now(),
    updated_at: Date.now()
  },

  // Experience Discovery - Inline Slider
  experience: {
    topic: "experience",
    icp_requirement: {
      section_id: "experience_range",
      section_name: "Years of Experience",
      bool_str: "(workexp.overall_experience:24-204)", // 2-17 years in months
      section_type: "Must Have"
    },
    status: DISCOVERY_STATUS.PENDING,
    discovery_config: {
      template_type: DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER,
      ai_intro_message: "Now let's determine the ideal experience level. I'll show you the current distribution to help guide your decision.",
      ai_confirmation_message: "Perfect! I've set your experience range preferences. This gives us a good target for candidate experience levels.",
      ux_config: {
        title: "Let's take a look at the ideal work experience for this role. Based on your search criteria, here's how your current employee base is distributed by years of experience. Go ahead and select a range below to refine your candidate search. 👇",
        chart_title: "Work Experience",
        confirm_button_text: "Confirm Work Experience"
      },
      template_props: {
        value: [2, 17],
        min: 0,
        max: 30,
        candidateCount: 12000,
        showSparkline: true
      }
    },
    discovery_result: null,
    created_at: Date.now(),
    updated_at: Date.now()
  }
};

/**
 * Queue Item Factory Functions
 */

/**
 * Create a new discovery queue item
 * @param {string} topic - Topic identifier
 * @param {Object} icpRequirement - ICP requirement object
 * @param {Object} discoveryConfig - Discovery configuration
 * @returns {Object} Complete queue item
 */
export const createQueueItem = (topic, icpRequirement, discoveryConfig) => {
  return {
    topic,
    icp_requirement: icpRequirement,
    status: DISCOVERY_STATUS.PENDING,
    discovery_config: discoveryConfig,
    discovery_result: null,
    created_at: Date.now(),
    updated_at: Date.now()
  };
};

/**
 * Create skills discovery queue item
 * @param {Array} skills - Array of skill names
 * @param {Array} selectedSkills - Pre-selected skills
 * @returns {Object} Skills queue item
 */
export const createSkillsQueueItem = (skills, selectedSkills = []) => {
  const boolStr = selectedSkills.length > 0 
    ? `(skills.name:(${selectedSkills.join(' OR ')}))`
    : `(skills.name:(${skills.join(' OR ')}))`;

  return createQueueItem("skills", {
    section_id: "skills_must_have",
    section_name: "Must Have Skills",
    bool_str: boolStr,
    section_type: "Must Have"
  }, {
    template_type: DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR,
    ai_intro_message: "Based on your role description, I've identified some key technical skills. Let me confirm these with you.",
    ai_confirmation_message: "Perfect! I've noted your required skills. This will help us find candidates with the right technical background.",
    ux_config: {
      title: "Got it! Just to confirm — you're looking for candidates with the following skills, right? 👇",
      confirm_button_text: "Confirm Skills"
    },
    template_props: {
      items: skills,
      selectedItems: selectedSkills
    }
  });
};

/**
 * Create location discovery queue item
 * @param {Array} locationSections - Array of location sections
 * @param {Array} selectedLocations - Pre-selected locations
 * @returns {Object} Location queue item
 */
export const createLocationQueueItem = (locationSections, selectedLocations = []) => {
  return createQueueItem("location", {
    section_id: "location_preferences", 
    section_name: "Location Preferences",
    bool_str: "(location.city:(San Francisco OR Vancouver) OR remote:true)",
    section_type: "Must Have"
  }, {
    template_type: DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR,
    ai_intro_message: "Now let's talk about location preferences. I've analyzed where similar candidates are typically found.",
    ai_confirmation_message: "Great choice! I've updated your location preferences. This will help us target the right talent pools.",
    ux_config: {
      title: "Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍\nPick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨",
      confirm_button_text: "Confirm Location"
    },
    template_props: {
      sections: locationSections,
      selectedItems: selectedLocations
    }
  });
};

/**
 * Create company stage discovery queue item
 * @param {Array} stageData - Array of stage data with percentages
 * @param {Array} selectedStages - Pre-selected stages
 * @param {Object} customConfig - Custom configuration overrides
 * @returns {Object} Company stage queue item
 */
export const createCompanyStageQueueItem = (stageData, selectedStages = [], customConfig = {}) => {
  const defaultConfig = {
    template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
    ai_intro_message: "Let me show you the company stage distribution of your current workforce to help guide your preferences.",
    ai_confirmation_message: "Excellent! I've noted your company stage preferences. This will help us find candidates with relevant experience.",
    ux_config: {
      title: "I reviewed your existing workforce, and this is the distribution across different company stages. Which company stage(s) would you like to target?",
      chartTitle: "Recent Company Stages",
      chartSubtitle: "Employees who recently worked at these company stages",
      confirm_button_text: "Select at Least One Option"
    },
    template_props: {
      data: stageData,
      selectedItems: selectedStages
    }
  };

  // Merge custom configuration
  const mergedConfig = {
    ...defaultConfig,
    ...customConfig,
    ux_config: {
      ...defaultConfig.ux_config,
      ...customConfig.ux_config
    },
    template_props: {
      ...defaultConfig.template_props,
      ...customConfig.template_props
    }
  };

  return createQueueItem("company_stage", {
    section_id: "company_stage_preferences",
    section_name: "Company Stage Experience", 
    bool_str: "(company.stage:(Late Stage OR Early Stage))",
    section_type: "Nice to Have"
  }, mergedConfig);
};

/**
 * Create experience discovery queue item
 * @param {Array} experienceRange - [min, max] years
 * @param {number} candidateCount - Number of candidates in range
 * @param {Object} customConfig - Custom configuration overrides
 * @returns {Object} Experience queue item
 */
export const createExperienceQueueItem = (experienceRange = [2, 17], candidateCount = 12000, customConfig = {}) => {
  const [minYears, maxYears] = experienceRange;
  const minMonths = minYears * 12;
  const maxMonths = maxYears * 12;

  const defaultConfig = {
    template_type: DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER,
    ai_intro_message: "Now let's determine the ideal experience level. I'll show you the current distribution to help guide your decision.",
    ai_confirmation_message: "Perfect! I've set your experience range preferences. This gives us a good target for candidate experience levels.",
    ux_config: {
      title: "Let's take a look at the ideal work experience for this role. Based on your search criteria, here's how your current employee base is distributed by years of experience. Go ahead and select a range below to refine your candidate search. 👇",
      chartTitle: "Work Experience",
      chartSubtitle: "",
      confirm_button_text: "Confirm Work Experience"
    },
    template_props: {
      value: experienceRange,
      min: 0,
      max: 30,
      candidateCount: candidateCount,
      showSparkline: true
    }
  };

  // Merge custom configuration
  const mergedConfig = {
    ...defaultConfig,
    ...customConfig,
    ux_config: {
      ...defaultConfig.ux_config,
      ...customConfig.ux_config
    },
    template_props: {
      ...defaultConfig.template_props,
      ...customConfig.template_props
    }
  };

  return createQueueItem("experience", {
    section_id: "experience_range",
    section_name: "Years of Experience",
    bool_str: `(workexp.overall_experience:${minMonths}-${maxMonths})`,
    section_type: "Must Have"
  }, mergedConfig);
};

/**
 * Validation functions
 */

/**
 * Validate queue item structure
 * @param {Object} queueItem - Queue item to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateQueueItem = (queueItem) => {
  const errors = [];
  
  // Required top-level fields
  if (!queueItem.topic) errors.push("Missing topic");
  if (!queueItem.icp_requirement) errors.push("Missing icp_requirement");
  if (!queueItem.status) errors.push("Missing status");
  if (!queueItem.discovery_config) errors.push("Missing discovery_config");
  
  // ICP requirement validation
  if (queueItem.icp_requirement) {
    if (!queueItem.icp_requirement.section_id) errors.push("Missing icp_requirement.section_id");
    if (!queueItem.icp_requirement.section_name) errors.push("Missing icp_requirement.section_name");
    if (!queueItem.icp_requirement.bool_str) errors.push("Missing icp_requirement.bool_str");
    if (!queueItem.icp_requirement.section_type) errors.push("Missing icp_requirement.section_type");
  }
  
  // Discovery config validation
  if (queueItem.discovery_config) {
    if (!queueItem.discovery_config.template_type) errors.push("Missing discovery_config.template_type");
    if (!queueItem.discovery_config.ux_config) errors.push("Missing discovery_config.ux_config");
    
    if (queueItem.discovery_config.ux_config) {
      if (!queueItem.discovery_config.ux_config.title) errors.push("Missing discovery_config.ux_config.title");
      if (!queueItem.discovery_config.ux_config.confirm_button_text) errors.push("Missing discovery_config.ux_config.confirm_button_text");
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  DISCOVERY_TEMPLATE_TYPES,
  DISCOVERY_STATUS,
  EXAMPLE_QUEUE_ITEMS,
  createQueueItem,
  createSkillsQueueItem,
  createLocationQueueItem,
  createCompanyStageQueueItem,
  createExperienceQueueItem,
  validateQueueItem
};
